import * as React from 'react'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Select from 'modules/core/components/Inputs/Select'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import i18n from 'modules/core/lib/i18n'

import type AllowanceForm from 'modules/timeoff/lib/AllowanceForm'
import {
  allowanceTypeOptionsLabel,
  roudingDaysOptionsLabel,
  roudingHoursOptionsLabel,
} from 'modules/timeoff/lib/dynamicTranslations'
import Allowance from 'modules/timeoff/store/models/Allowance'

interface Props {
  allowance: Allowance
  allowanceForm: AllowanceForm
  isHourly: boolean
  isOvertime: boolean
  isTimeWorked: boolean
}

const AllowanceTypeAndRounding = ({
  allowance,
  allowanceForm,
  isHourly,
  isOvertime,
  isTimeWorked,
}: Props) => {
  const allowanceTypes = useLazyRef(() =>
    Object.keys(Allowance.ALLOWANCE_TYPES).map((value) => ({
      label: allowanceTypeOptionsLabel(value),
      value,
    }))
  ).current

  const roundingDaysOptions = Object.keys(Allowance.ROUNDING_OPTIONS_DAYS).map((value) => ({
    label: roudingDaysOptionsLabel(value),
    value,
  }))

  const roundingHoursOptions = Object.keys(Allowance.ROUNDING_OPTIONS_HOURS).map((value) => ({
    label: roudingHoursOptionsLabel(value),
    value,
  }))

  const roundingOptions = isHourly ? roundingHoursOptions : roundingDaysOptions

  const roundingLabel = isHourly
    ? i18n.t('time_off.allowances.page.labels.rounding_hours')
    : i18n.t('time_off.allowances.page.labels.rounding_days')

  const roundingInformation = isHourly
    ? i18n.t('time_off.allowances.page.information.rounding_hours')
    : i18n.t('time_off.allowances.page.information.rounding_days')

  const isOuvrablesText = allowanceForm.isOuvrables
    ? i18n.t('time_off.allowances.page.information.allowance_type_french')
    : i18n.t('time_off.allowances.page.information.allowance_type')

  const onChangeAllowanceType = () => {
    allowanceForm.resetRounding()
  }

  const disableAllowanceType = !allowance.isNew || isOvertime || isTimeWorked

  return (
    <>
      <Select
        field={allowanceForm.get('allowance_type')}
        label={i18n.t('time_off.allowances.page.labels.allowance_type')}
        secondaryLabel={<InformationTooltip information={isOuvrablesText} icon="question" />}
        options={allowanceTypes}
        readonly={disableAllowanceType}
        onChange={() => onChangeAllowanceType()}
        extra={{ 'data-intercom-target': 'timeoff-allowances-allowance-type-input' }}
      />
      <Select
        field={allowanceForm.get('rounding')}
        label={roundingLabel}
        secondaryLabel={<InformationTooltip information={roundingInformation} icon="question" />}
        options={roundingOptions}
        extra={{ 'data-intercom-target': 'timeoff-allowances-rounding-input' }}
      />
    </>
  )
}

export default observer(AllowanceTypeAndRounding)
